import {
  type RefObject,
  useEffect,
  useEffectEvent,
  useRef,
  useState,
} from "react";
import { isEditableTarget } from "../../lib/isEditableTarget";
import type { LineOrigin } from "../stepFilesDiff";
import type { CodeEditorHandle } from ".";

type Hunk = { fromDocLine: number; toDocLine: number };

function computeHunks(origins: readonly LineOrigin[]): Hunk[] {
  const out: Hunk[] = [];
  let start = -1;
  origins.forEach((o, i) => {
    if (o.kind !== "unchanged") {
      if (start === -1) start = i;
    } else if (start !== -1) {
      out.push({ fromDocLine: start + 1, toDocLine: i });
      start = -1;
    }
  });
  if (start !== -1) {
    out.push({ fromDocLine: start + 1, toDocLine: origins.length });
  }
  return out;
}

export function useCodeDiffNavigation({
  enabled,
  files,
  selectedPath,
  onSelectPath,
  lineOrigins,
  editorRef,
  resetKey = String(enabled),
  streaming = false,
}: {
  enabled: boolean;
  files: readonly { path: string }[];
  selectedPath: string | undefined;
  onSelectPath: (path: string) => void;
  lineOrigins: readonly LineOrigin[] | undefined;
  editorRef: RefObject<CodeEditorHandle | null>;
  resetKey?: string;
  streaming?: boolean;
}) {
  const currentHunks = computeHunks(lineOrigins ?? []);
  const [selection, setSelection] = useState({ selectedPath, resetKey });
  const [currentHunkIndex, setCurrentHunkIndex] = useState(0);
  if (
    selection.selectedPath !== selectedPath ||
    selection.resetKey !== resetKey
  ) {
    setSelection({ selectedPath, resetKey });
    setCurrentHunkIndex(0);
  } else if (enabled && currentHunkIndex >= currentHunks.length) {
    const clampedIndex = Math.max(0, currentHunks.length - 1);
    if (currentHunkIndex !== clampedIndex) setCurrentHunkIndex(clampedIndex);
  }

  const fileCount = files.length;
  const fileIndex = files.findIndex((file) => file.path === selectedPath);
  const currentHunksRef = useRef(currentHunks);
  const hasInitializedScrollRef = useRef(false);
  useEffect(() => {
    currentHunksRef.current = currentHunks;
  });

  useEffect(() => {
    hasInitializedScrollRef.current = false;
  }, [selectedPath, resetKey]);

  // Streaming owns the scroll position until the reader changes files or reopens the diff.
  useEffect(() => {
    if (streaming) {
      hasInitializedScrollRef.current = true;
      return;
    }
    if (!enabled || hasInitializedScrollRef.current) return;
    hasInitializedScrollRef.current = true;
    const hunk = currentHunksRef.current[0];
    if (hunk) editorRef.current?.scrollToLine(hunk.fromDocLine);
  }, [selectedPath, resetKey, enabled, streaming, editorRef]);

  const goNextFile = () => {
    if (fileCount === 0) return;
    const nextIndex = (fileIndex + 1) % fileCount;
    if (nextIndex !== fileIndex) onSelectPath(files[nextIndex]!.path);
  };

  const goPrevFile = () => {
    if (fileCount === 0) return;
    const startIndex = fileIndex >= 0 ? fileIndex : 0;
    const previousIndex = (startIndex - 1 + fileCount) % fileCount;
    if (previousIndex !== fileIndex) onSelectPath(files[previousIndex]!.path);
  };

  const scrollToHunk = (index: number) => {
    const hunk = currentHunks[index];
    if (!hunk) return;
    setCurrentHunkIndex(index);
    editorRef.current?.scrollToLine(hunk.fromDocLine);
  };

  const goNextDiff = () => {
    if (currentHunks.length === 0) return;
    scrollToHunk((currentHunkIndex + 1) % currentHunks.length);
  };

  const goPrevDiff = () => {
    if (currentHunks.length === 0) return;
    scrollToHunk(
      (currentHunkIndex - 1 + currentHunks.length) % currentHunks.length
    );
  };

  const handleVisibleLineChange = (line: number | null) => {
    if (line == null) return;
    // Smooth scrolling can settle just above the hunk, so allow two lines of tolerance.
    const tolerance = 2;
    let foundIndex = 0;
    for (let i = 0; i < currentHunks.length; i++) {
      if (currentHunks[i]!.fromDocLine - tolerance <= line) {
        foundIndex = i;
      } else {
        break;
      }
    }
    setCurrentHunkIndex(foundIndex);
  };

  const handleKeyDown = useEffectEvent((e: KeyboardEvent) => {
    if (isEditableTarget(e)) return;
    if (e.metaKey || e.ctrlKey || e.altKey) return;
    if (e.key === "n") {
      e.preventDefault();
      goNextDiff();
    } else if (e.key === "p") {
      e.preventDefault();
      goPrevDiff();
    }
  });

  useEffect(() => {
    if (!enabled || fileCount === 0) return;
    const handler = (e: KeyboardEvent) => handleKeyDown(e);
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [enabled, fileCount]);

  return {
    fileCount,
    fileIndex,
    diffCount: currentHunks.length,
    diffIndex: currentHunkIndex,
    goNextFile,
    goPrevFile,
    goNextDiff,
    goPrevDiff,
    onVisibleLineChange: enabled ? handleVisibleLineChange : undefined,
  };
}
